"use client";

import { useClientConfig } from "@providers/app-provider/client-app-provider";
import { StorageBucket, StorageFile } from "@sisnaker-react-core/karirhub-model-type";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export function useStorageFile() {
  const config = useClientConfig();
  const apiUrl = config?.config?.apiUrl;
  const token = config?.accessToken;

  async function fetchFileDetails(storageCode: StorageBucket, fileId: string): Promise<StorageFile | null> {
    const response = await fetch(`${apiUrl}/storage/v1/storages/${storageCode}/files/${fileId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error("Gagal mengambil detail file.");
    }

    const result = await response.json();
    const data = result?.data;

    if (!data) return null;

    return {
      id: data.id,
      name: data.name,
      download_link: data.download_link || "",
      bucket: data.bucket || { id: "", name: "", code: "" },
      bucket_id: data.bucket_id || "",
      user: null,
      user_id: data.user_id || "",
      created_at: data.created_at,
      updated_at: data.updated_at,
    } as StorageFile;
  }

  function getFile({ bucket, fileId }: { bucket: StorageBucket; fileId: string | undefined }) {
    const query = useQuery<StorageFile | null>({
      queryKey: ["storage-file-", bucket, fileId],
      queryFn: () => fetchFileDetails(bucket, fileId!),
      enabled: !!bucket && !!fileId,
    });

    return {
      data: query.data,
      query,
      refetch: query.refetch,
    };
  }

  function getFiles({ bucket, fileIds }: { bucket: StorageBucket; fileIds: string[] }) {
    const queries = useQueries({
      queries: fileIds.map((fileId) => ({
        queryKey: ["storage-file", bucket, fileId],
        queryFn: () => fetchFileDetails(bucket, fileId),
        enabled: !!bucket && (fileIds && fileIds.length > 0),
      })),
    });

    return {
      data: queries
        .map((q) => q.data)
        .filter(Boolean) as StorageFile[],
      queries,
      isLoading: queries.some((q) => q.isLoading),
      isError: queries.some((q) => q.isError),
      refetch: () => Promise.all(queries.map((q) => q.refetch())),
    };
  }

  /**
   * Fetches a single file and returns it as a memoized array ready to be passed
   * to ChunkStorageFileUploader's `initialFiles`. The reference only changes
   * when the fetched file changes, so the uploader's sync effect is not
   * re-triggered on every render (which would re-add a removed file).
   */
  function getInitialFile({ bucket, fileId }: { bucket: StorageBucket; fileId: string | undefined }) {
    const { data } = getFile({ bucket, fileId });
    return useMemo(() => (data ? [data] : undefined), [data]);
  }

  /**
   * Fetches multiple files and returns them as a memoized array ready to be
   * passed to ChunkStorageFileUploader's `initialFiles`. Each query's `data`
   * is referentially stable, so they are used as the memo deps directly — the
   * returned array reference only changes when a fetched file changes, so the
   * uploader's sync effect is not re-triggered on every render.
   */
  function getInitialFiles({ bucket, fileIds }: { bucket: StorageBucket; fileIds: string[] }) {
    const { queries } = getFiles({ bucket, fileIds });
    const fileDatas = queries.map((q) => q.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => fileDatas.filter(Boolean) as StorageFile[], fileDatas);
  }

  return { fetchFileDetails, getFile, getFiles, getInitialFile, getInitialFiles };
}
